import { c as _c } from "react/compiler-runtime";
import 'mapbox-gl/dist/mapbox-gl.css';
import themes from '@onefootprint/design-tokens';
import { useTheme } from 'next-themes';
import React, { type JSX, useEffect, useRef, useState } from 'react';
import type { MapRef } from 'react-map-gl';
import { Layer, Map as MapboxMap, Marker, Source } from 'react-map-gl';
import { MAP_BOX_TOKEN } from '@/config/constants';
import type { MapMarkerProps } from './components/map-marker';
const DEFAULT_ZOOM = 11;
const MAX_ZOOM = 20;
const MIN_ZOOM = 1;
const FLY_ANIMATION_DURATION = 1000;
// The padding clears a pin's 40px height above its point.
const FIT_BOUNDS_OPTIONS = {
  padding: 48,
  maxZoom: DEFAULT_ZOOM
};

// Mapbox marks each marker's wrapper as an image named "Map marker", which hides the pin button inside it, and its
// own name and pressed state, from screen readers.
const exposeMarkerContent = (marker: {
  getElement: () => HTMLElement;
} | null) => {
  const element = marker?.getElement();
  element?.removeAttribute('role');
  element?.removeAttribute('aria-label');
};
type Coords = {
  lat: number;
  lng: number;
};
type MapLine = {
  id: string;
  from: Coords;
  to: Coords;
};
type MapProps = {
  markers: JSX.Element[];
  lines?: MapLine[];
  selectedCoords: Coords;
  /** Counts picks. Before the first the map frames every pin; each one flies to the selection, even at the same spot. */
  focusKey?: number;
  onSelect: (id: string) => void;
};
const MapComponent = t0 => {
  const $ = _c(54);
  const {
    markers,
    lines: t1,
    selectedCoords,
    focusKey,
    onSelect
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const lines = t2;
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const {
    resolvedTheme
  } = useTheme();
  const isDark = resolvedTheme === "dark";
  let t3;
  if ($[2] !== lines) {
    t3 = lines.map(_temp);
    $[2] = lines;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = {
      type: "FeatureCollection" as const,
      features: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const linesData = t4;
  let east;
  let south;
  let t5;
  let west;
  if ($[6] !== markers) {
    const pins = markers.map(_temp2);
    west = Math.min(...pins.map(_temp3));
    east = Math.max(...pins.map(_temp4));
    south = Math.min(...pins.map(_temp5));
    t5 = Math.max(...pins.map(_temp6));
    $[6] = markers;
    $[7] = east;
    $[8] = south;
    $[9] = t5;
    $[10] = west;
  } else {
    east = $[7];
    south = $[8];
    t5 = $[9];
    west = $[10];
  }
  const north = t5;
  let t6;
  let t7;
  if ($[11] !== east || $[12] !== focusKey || $[13] !== isMapReady || $[14] !== north || $[15] !== south || $[16] !== west) {
    t6 = () => {
      if (isMapReady && !focusKey) {
        mapRef.current?.fitBounds([[west, south], [east, north]], {
          ...FIT_BOUNDS_OPTIONS,
          duration: FLY_ANIMATION_DURATION
        });
      }
    };
    t7 = [isMapReady, focusKey, west, south, east, north];
    $[11] = east;
    $[12] = focusKey;
    $[13] = isMapReady;
    $[14] = north;
    $[15] = south;
    $[16] = west;
    $[17] = t6;
    $[18] = t7;
  } else {
    t6 = $[17];
    t7 = $[18];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[19] !== focusKey || $[20] !== isMapReady || $[21] !== selectedCoords.lat || $[22] !== selectedCoords.lng) {
    t8 = () => {
      if (isMapReady && focusKey) {
        mapRef.current?.flyTo({
          center: [selectedCoords.lng, selectedCoords.lat],
          zoom: DEFAULT_ZOOM,
          duration: FLY_ANIMATION_DURATION
        });
      }
    };
    t9 = [isMapReady, selectedCoords.lat, selectedCoords.lng, focusKey];
    $[19] = focusKey;
    $[20] = isMapReady;
    $[21] = selectedCoords.lat;
    $[22] = selectedCoords.lng;
    $[23] = t8;
    $[24] = t9;
  } else {
    t8 = $[23];
    t9 = $[24];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const observer = new ResizeObserver(() => mapRef.current?.resize());
      observer.observe(container);
      return () => observer.disconnect();
    };
    t11 = [];
    $[25] = t10;
    $[26] = t11;
  } else {
    t10 = $[25];
    t11 = $[26];
  }
  useEffect(t10, t11);
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setIsMapReady(true);
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = isDark ? "mapbox://styles/mapbox/dark-v11" : "mapbox://styles/mapbox/streets-v12";
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      width: "100%",
      height: "100%"
    };
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== south || $[30] !== west) {
    t15 = [west, south];
    $[29] = south;
    $[30] = west;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== east || $[33] !== north) {
    t16 = [east, north];
    $[32] = east;
    $[33] = north;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = {
      bounds: [t15, t16],
      fitBoundsOptions: FIT_BOUNDS_OPTIONS
    };
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const t18 = isDark ? themes.dark : themes.light;
  let t19;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = [2, 2];
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t18.color.tertiary) {
    t20 = <Layer id="lines" type="line" paint={{
      "line-color": t18.color.tertiary,
      "line-width": 2,
      "line-dasharray": t19
    }} />;
    $[39] = t18.color.tertiary;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== linesData || $[42] !== t20) {
    t21 = <Source id="lines" type="geojson" data={linesData}>{t20}</Source>;
    $[41] = linesData;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== markers || $[45] !== onSelect) {
    let t23;
    if ($[47] !== onSelect) {
      t23 = marker_0 => {
        const {
          id,
          lat: lat_1,
          lng: lng_1,
          isSelected
        } = marker_0.props as MapMarkerProps;
        return <Marker key={marker_0.key} ref={exposeMarkerContent} longitude={lng_1} latitude={lat_1} anchor="bottom" style={isSelected ? {
          zIndex: 20
        } : {
          zIndex: 0
        }}>{React.cloneElement(marker_0, {
            onClick: () => onSelect(id)
          })}</Marker>;
      };
      $[47] = onSelect;
      $[48] = t23;
    } else {
      t23 = $[48];
    }
    t22 = markers.map(t23);
    $[44] = markers;
    $[45] = onSelect;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[49] !== t13 || $[50] !== t17 || $[51] !== t21 || $[52] !== t22) {
    t23 = <div ref={containerRef} className="flex w-full h-full"><MapboxMap ref={mapRef} onLoad={t12} reuseMaps={true} mapboxAccessToken={MAP_BOX_TOKEN} mapStyle={t13} style={t14} initialViewState={t17} maxZoom={MAX_ZOOM} minZoom={MIN_ZOOM}>{t21}{t22}</MapboxMap></div>;
    $[49] = t13;
    $[50] = t17;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  return t23;
};
export type { MapLine, MapProps };
export default MapComponent;
function _temp(line) {
  return {
    type: "Feature" as const,
    properties: {
      id: line.id
    },
    geometry: {
      type: "LineString" as const,
      coordinates: [[line.from.lng, line.from.lat], [line.to.lng, line.to.lat]]
    }
  };
}
function _temp2(marker) {
  return marker.props as MapMarkerProps;
}
function _temp3(t0) {
  const {
    lng
  } = t0;
  return lng;
}
function _temp4(t0) {
  const {
    lng: lng_0
  } = t0;
  return lng_0;
}
function _temp5(t0) {
  const {
    lat
  } = t0;
  return lat;
}
function _temp6(t0) {
  const {
    lat: lat_0
  } = t0;
  return lat_0;
}